import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../store/store";
import {
  Button,
  Image,
  Input,
  Popconfirm,
  Space,
  Modal,
  Form,
  InputNumber,
} from "antd";
import {
  PlusOutlined,
  AppstoreOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { PATH_STORE } from "../config";
import CustomPaginatedTable from "../components/CustomPaginatedTable";
import { queryClient } from "../store/queryClient";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryApi } from "../store/actions/common";
import moment from "moment";
import ModalSlots from "../components/modal/ModalSlots";
import PageHeader from "../components/PageHeader";
import { triggerNotification } from "../store/directActions";
import { Move, Pause, Play } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import useSocket from "../hooks/useSocket";
import { useLocalState } from "../hooks/useLocalStorage";
import { sortByIsPause } from "../utils/mapper";

interface OwnProps {}

type configurationPageProps = OwnProps & PropsFromRedux;

const PAGE_TYPE = "expense";
const VIEW_MODE_STORAGE_KEY = "shared-page-view-mode";

const ConfigurationPage: React.FC<configurationPageProps> = ({}) => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveSlotId, setMoveSlotId] = useState<string | null>(null);
  const [filterData, setFilterData] = useState({});
  const [amount, setAmount] = useState("");
  const [viewMode, setViewMode] = useLocalState(VIEW_MODE_STORAGE_KEY, "card");
  const [moveForm] = Form.useForm();
  const moveInputRef = useRef<HTMLInputElement>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { sendMessage } = useSocket();

  useEffect(() => {
    if (location.state?.openModal) {
      updateDrawer(true);
    }
  }, [location?.state?.openModal]);

  // Fetch store data for config and meta
  const { data: storeData, isPending: isStorePending } = useQuery({
    queryKey: [PATH_STORE, { type: "current" }],
    queryFn: () => queryApi(`store/user`, "GET"),
    staleTime: Infinity,
    gcTime: 0,
  });

  // Fetch store data for config and meta
  const { data: storeConfig, isPending: isStoreConfigPending } = useQuery({
    queryKey: [PATH_STORE, "config", { type: "current" }],
    queryFn: () => queryApi(`store/config`, "GET"),
    staleTime: Infinity,
    gcTime: 0,
  });

  // Slots for checkDataBox, sourced from the table's own fetch below so we
  // don't fire a second request for the same list on mount.
  const [allSlotsData, setAllSlotsData] = useState<any[]>([]);

  // Mutation for pausing boxes
  const { mutate: pauseBoxes } = useMutation({
    mutationFn: (id: string) => queryApi(`slot/pause/${id}`, "PUT", {}),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["slot"],
      });
      sendMessage("screen-refresh");
      triggerNotification({
        title: "Success",
        description: "Box paused successfully",
        type: "success",
      });
    },
    onError: (error: any) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to pause box",
        type: "error",
      });
    },
  });

  // Mutation for moving boxes
  const { mutate: moveBox, isPending: isMoving } = useMutation({
    mutationFn: ({
      slotId,
      slotNumber,
    }: {
      slotId: string;
      slotNumber: string;
    }) => queryApi(`slot/move/${slotId}/${slotNumber}`, "PUT", {}),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["slot"],
      });
      sendMessage("screen-refresh");
      setShowMoveModal(false);
      moveForm.resetFields();
      setMoveSlotId(null);
      triggerNotification({
        title: "Success",
        description: "Box moved successfully",
        type: "success",
      });
    },
    onError: (error: any) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to move box",
        type: "error",
      });
    },
  });

  const updateDrawer = (state: boolean = false) => {
    setShowDrawer(state);
    if (!state)
      navigate(location.pathname, {
        replace: true,
        state: null,
      });
  };

  const handleMoveBox = (record: any) => {
    setMoveSlotId(record.slot_id);
    setShowMoveModal(true);
    moveForm.setFieldsValue({
      slot_number: "",
    });
  };

  const onMoveFormSubmit = (values: { slot_number: string }) => {
    if (moveSlotId) {
      moveBox({
        slotId: moveSlotId,
        slotNumber: values.slot_number,
      });
    }
  };

  const customRender = (item: any, index: number, globalIndex: number) => {
    return (
      <div
        key={item.slot_id || index}
        className="app-surface rounded-xl shadow-sm border border-solid app-border p-6 hover:shadow-md transition-shadow duration-200 mb-4"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-4 flex-1">
            {item.image_url && (
              <Image
                width={80}
                height={80}
                src={item.image_url.replace("original", "original")}
                className="rounded-lg object-cover"
              />
            )}
            <div className="flex-1">
              <h3 className="text-lg font-semibold app-text mb-2">
                {item.name}
              </h3>
              <div className="inline-flex items-center px-3 py-1 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
                  Box #{item.slot_number}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm app-muted">Game #</p>
            <p className="text-lg font-semibold app-text">
              {item.game_number}
            </p>
          </div>
          <div>
            <p className="text-sm app-muted">Pack #</p>
            <p className="text-lg font-semibold app-text">
              {item.pack}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm app-muted">
              Current #
            </p>
            <p className="text-lg font-semibold"
            style={{ color: "var(--dash-value)" }}>
              {item.current_no}
            </p>
          </div>
          <div>
            <p className="text-sm app-muted">
              Date Updated
            </p>
            <p className="text-sm font-medium app-text">
              {moment(item.updated_at).format("MMMM DD, YYYY")}
            </p>
          </div>
        </div>

        <div className="flex justify-between items-center pt-4 border-0 border-t border-solid app-border">
          <Popconfirm
            title="Are you sure to move this box to new box # ?"
            onConfirm={() => {
              handleMoveBox(item);
            }}
            onCancel={() => {}}
            okText="Yes"
            cancelText="No"
          >
            <div className="flex items-center space-x-1 text-sm font-medium cursor-pointer"
              style={{ color: "var(--dash-link)" }}
            >
              {item.is_paused ? (
                <Play className="h-4 w-4" />
              ) : (
                <Move className="h-4 w-4" />
              )}
              <span>{item.is_paused ? "Resume" : "Move"}</span>
            </div>
          </Popconfirm>
          {!item.is_paused && (
            <Popconfirm
              title="Are you sure to pause this box ?"
              onConfirm={() => {
                pauseBoxes(item.slot_id);
              }}
              onCancel={() => {}}
              okText="Yes"
              cancelText="No"
            >
              <div className="flex items-center space-x-1 text-sm font-medium cursor-pointer"
              style={{ color: "var(--dash-link)" }}
            >
                <Pause className="h-4 w-4" />
                <span> Pause</span>
              </div>
            </Popconfirm>
          )}
        </div>
      </div>
    );
  };

  const columns = [
    {
      title: <div style={{ minWidth: "max-content" }}>Box #</div>,
      dataIndex: "slot_number",
      key: "slot_number",
      sorter: (a, b) => (a.slot_number ?? 0) - (b.slot_number ?? 0),
      render: (slot_number, record, index) => <div>#{slot_number}</div>,
      width: 70,
    },
    {
      title: "Image",
      dataIndex: "image_url",
      key: "image_url",
      render: (image_url) => (
        <Image
          width={40}
          height={40}
          src={image_url.replace("original", "original")}
        />
      ),
      width: 70,
    },
    // {
    //   title: "Provider",
    //   dataIndex: "provider",
    //   key: "provider",
    //   render: (item, record, index) => <div>{item.name}</div>,
    // },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name ?? "").localeCompare(b.name ?? ""),
      // width: 400,
    },
    {
      title: <div style={{ minWidth: "max-content" }}>Current #</div>,
      dataIndex: "current_no",
      key: "current_no",
      // title: "current #",
      width: 150,
      sorter: (a, b) => (a.current_no ?? 0) - (b.current_no ?? 0),
    },
    {
      title: <div style={{ minWidth: "max-content" }}>Game-Pack #</div>,
      dataIndex: "game_number",
      key: "game_number",
      width: 150,
      sorter: (a, b) =>
        (a.game_number ?? 0) - (b.game_number ?? 0) ||
        (a.pack ?? 0) - (b.pack ?? 0),
      render: (item, record, index) => (
        <div style={{ minWidth: "max-content" }}>
          {record.game_number}-{record.pack}
        </div>
      ),
    },
    {
      title: "Date Updated",
      dataIndex: "updated_at",
      key: "updated_at",
      sorter: (a, b) =>
        moment(a.updated_at).valueOf() - moment(b.updated_at).valueOf(),
      render: (item, record, index) => (
        <div>{moment(item).format("MMMM DD, YYYY")}</div>
      ),
      width: 150,
    },

    // {
    //   title: "Update Box",
    //   key: "is_closed",
    //   render: (item, record, index) => (
    //     <Space size="middle">

    //     </Space>
    //   ),
    // },

    // {
    //   title: "Pause",
    //   key: "is_paused",
    //   render: (item, record, index) => (
    //     <Space size="middle">

    //     </Space>
    //   ),
    // },

    // {
    //   title: "Discontinue",
    //   key: "is_closed",
    //   render: (item, record, index) => (
    //     <Space size="middle">
    //       <Popconfirm
    //         title={
    //           <div style={{ fontWeight: 600 }}>
    //             Please make sure Current Numbers for all Packs are as expected.
    //             <br></br>Are you sure you want to mark this Game as
    //             Discontinued?
    //           </div>
    //         }
    //         onConfirm={() => onUpdateItem(record, { is_closed: true })}
    //         onCancel={() => {}}
    //         okText="Yes"
    //         cancelText="No"
    //       >
    //         <a href="#" style={{ color: "red" }}>
    //           Discontinue
    //         </a>
    //       </Popconfirm>
    //     </Space>
    //   ),
    // },

    // {
    //   title: "Amount",
    //   dataIndex: "amount",
    //   key: "amount",
    //   render: (item, record, index) => (
    //     <div>
    //       {record.currency}
    //       {record.amount}
    //     </div>
    //   ),
    // },
    // {
    //   title: "Pack Size",
    //   dataIndex: "pack_size",
    //   key: "pack_size",
    // },
    // {
    //   title: "Open Date",
    //   dataIndex: "open_date",
    //   key: "open_date",
    // },
    //   {
    //     title: "Close Date",
    //     dataIndex: "close_date",
    //     key: "top_pclose_daterize",
    //   },

    // {
    //   title: "Top Prize",
    //   dataIndex: "top_prize",
    //   key: "top_prize",
    //   render: (item, record, index) => (
    //     <div>
    //       {record.currency}
    //       {record.top_prize}
    //     </div>
    //   ),
    // },

    {
      title: "Action",
      key: "action",
      render: (record) => (
        <Space size="middle" style={{ display: "flex" }}>
          <Popconfirm
            title="Are you sure to move this box to new box # ?"
            onConfirm={() => {
              handleMoveBox(record);
            }}
            onCancel={() => {}}
            okText="Yes"
            cancelText="No"
          >
            <a
              href="#"
              style={{ color: "var(--dash-link)" }}
            >
              {record.is_paused ? "Resume" : "Move"}
            </a>
          </Popconfirm>
          <Popconfirm
            title="Are you sure to pause this box ?"
            onConfirm={() => {
              console.log(record);
              pauseBoxes(record.slot_id);
            }}
            onCancel={() => {}}
            okText="Yes"
            cancelText="No"
          >
            <a
              href="#"
              style={{ color: "var(--dash-link)" }}
            >
              {record.is_paused ? "" : "Pause"}
            </a>
          </Popconfirm>
          {/* <a onClick={() => onActionItem(record)}>Edit</a> */}
          {/* <Popconfirm
            title="Are you sure to close this?"
            onConfirm={() => onUpdateItem(record, { is_closed: true })}
            onCancel={() => {
              console.log(record);
            }}
            okText="Yes"
            cancelText="No"
          >
            <a href="#" style={{ color: "red" }}>
              Close
            </a>
             <DeleteOutlined style={{ fontSize: 22, color: "red" }} /> 
          </Popconfirm> */}
        </Space>
      ),
      width: 150,
    },
  ];
  return (
    <section id="customers" className="page-section h-page p-6 flex flex-col">
      <PageHeader
        title=""
        extra={[
          <Input
            key="search"
            allowClear
            style={{ width: "300px" }}
            defaultValue=""
            placeholder="Search with Name, Game# or Pack#"
            onChange={(e) => {
              setAmount(e.target.value);
            }}
          />,
          <Button
            key="add"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() =>
              navigate(location.pathname, {
                replace: true,
                state: { openModal: true },
              })
            }
          >
            Add
          </Button>,
          <Button.Group key="view-toggle">
            <Button
              type={viewMode === "table" ? "primary" : "default"}
              icon={<TableOutlined />}
              onClick={() => setViewMode("table")}
            >
              Table
            </Button>
            <Button
              type={viewMode === "card" ? "primary" : "default"}
              icon={<AppstoreOutlined />}
              onClick={() => setViewMode("card")}
            >
              Card
            </Button>
          </Button.Group>,
        ]}
      />

      {/* @ts-ignore */}
      <CustomPaginatedTable
        columns={columns}
        hidePagination={true}
        filterData={filterData}
        defaultSort={sortByIsPause}
        customDataMapper={(data) =>
          data.map((item: any) => ({ ...item.lottery, ...item }))
        }
        localFilterFunction={(data) => {
          return data?.filter(
            (item) =>
              ("" + item.slot_number).includes(amount) ||
              ("" + item.current_no).includes(amount) ||
              ("" + item.game_number).includes(amount) ||
              item.name?.toLowerCase().includes(amount?.toLowerCase()) ||
              ("" + item.pack).includes(amount),
          );
        }}
        queryKey={["slot", { type: "all" }]}
        path={"slot/"}
        // Receives the unfiltered fetched list (before localFilterFunction), so
        // the search box never shrinks the duplicate-slot validation set. If
        // server-side filterData is ever wired up here, revisit this.
        getDataStats={setAllSlotsData}
        customRender={viewMode === "card" ? customRender : null}
        customWrapperClass={
          viewMode === "card"
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            : undefined
        }
        tableProps={
          viewMode === "table"
            ? {
                sticky: true,
                tableLayout: "auto",
                scroll: { y: true, x: true },
              }
            : undefined
        }
      />

      <ModalSlots
        showModal={showDrawer}
        toggleModal={updateDrawer}
        onSuccess={() => {
          // queryClient.invalidateQueries({
          //   queryKey: ["slot"],
          // });
        }}
        checkDataBox={(allSlotsData || []) as any}
        storeConfig={storeConfig ?? {}}
        storeMeta={storeData?.[0] ?? {}}
      />

      <Modal
        title="Move Box"
        open={showMoveModal}
        afterOpenChange={(open) => {
          if (open) moveInputRef.current?.focus();
        }}
        onOk={() => moveForm.submit()}
        onCancel={() => {
          setShowMoveModal(false);
          moveForm.resetFields();
          setMoveSlotId(null);
        }}
        okButtonProps={{ loading: isMoving }}
        cancelButtonProps={{ disabled: isMoving }}
        maskClosable={false}
      >
        <Form
          form={moveForm}
          onFinish={onMoveFormSubmit}
          layout="vertical"
          hideRequiredMark
        >
          <Form.Item
            name="slot_number"
            label="BOX #"
            rules={[
              { required: true, message: "Please enter Box #" },
              {
                pattern: /^\d+$/,
                message: "Box # must be a number",
              },
            ]}
          >
            <InputNumber
              ref={moveInputRef}
              placeholder="Enter new Box #"
              autoFocus
              style={{ width: "100%" }}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  moveForm.submit();
                }
              }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Expenses Table */}
      {/* <div className="app-surface rounded-lg shadow-sm border app-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="app-surface-alt border-b app-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Vehicle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Receipt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="app-surface divide-y divide-gray-200">
              {expenses.map((expense) => (
                <tr key={expense.id} className="app-hover">
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-text">
                    {new Date(expense.date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-text">
                    <div className="flex items-center gap-2">
                      <Car className="w-4 h-4 app-muted" />
                      {expense.vehicle}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(
                        expense.category
                      )}`}
                    >
                      {getCategoryIcon(expense.category)}
                      {expense.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm app-text max-w-xs truncate">
                    {expense.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold app-text">
                    ${expense.amount.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-muted">
                    {expense.receipt ? (
                      <span className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer">
                        View Receipt
                      </span>
                    ) : (
                      <span className="app-muted">No receipt</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-muted">
                    <div className="flex items-center gap-2">
                      <button
                        // onClick={() => handleEdit(expense)}
                        className="text-blue-600 hover:text-blue-900 transition-colors"
                      >
                        <Icon name="Edit2" className="w-4 h-4" />
                      </button>
                      <button
                        // onClick={() => handleDelete(expense.id)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                      >
                        <Icon name="Trash2" className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div> */}

      {/* Add/Edit Modal */}
      {/* {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="app-surface rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold app-text mb-4">
                {editingExpense ? "Edit Expense" : "Add New Expense"}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    required
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Vehicle
                  </label>
                  <select
                    required
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.vehicle}
                    onChange={(e) =>
                      setFormData({ ...formData, vehicle: e.target.value })
                    }
                  >
                    <option value="">Select Vehicle</option>
                    {vehicles.map((vehicle) => (
                      <option key={vehicle} value={vehicle}>
                        {vehicle}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Category
                  </label>
                  <select
                    required
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Enter expense description"
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Amount ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    placeholder="0.00"
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData({ ...formData, amount: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Receipt (optional)
                  </label>
                  <input
                    type="text"
                    placeholder="Receipt filename or reference"
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.receipt}
                    onChange={(e) =>
                      setFormData({ ...formData, receipt: e.target.value })
                    }
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-2 app-text app-surface-alt border app-border rounded-lg app-hover transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {editingExpense ? "Update" : "Add"} Expense
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )} */}
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  // Map state properties to props here if needed
  data: state.cms?.[PAGE_TYPE],
  storeId: state.user?.profileData?.storeId,
  REFRESH: state.levent?.[PAGE_TYPE],
});

const mapDispatchToProps = (dispatch: any) => ({
  // login: (data: any) => dispatch(getProfile(data)),
  fetchCms: (reqData: any) =>
    dispatch(fetchData(PATH_MANAGE, PATH, reqData, PAGE_TYPE)),
  generateToken: (id: any) =>
    dispatch(fetchData(PATH_MANAGE, `${PATH}/login/app/${id}`, {}, "")),
  createData: (reqData: any) =>
    dispatch(postData(PATH_MANAGE, `${PATH}`, reqData, PAGE_TYPE)),

  editData: (id: string, reqData: any) =>
    dispatch(putData(PATH_MANAGE, `${PATH}/`, id, reqData, PAGE_TYPE)),
  removeCms: (reqData: any) =>
    dispatch(deleteData(PATH_MANAGE, `${PATH}/`, reqData, PAGE_TYPE)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

// Define the props that connect will inject into the component
type PropsFromRedux = ConnectedProps<typeof connector>;

// Connect the component to Redux
export default connector(ConfigurationPage);
